import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath } from 'react-router-dom';
import { Tab, Row, Col, Modal } from 'react-bootstrap';
import { SubNav } from '../layout/submenu';
import { getOperatorList, deletePerson } from '../../actions/person';
import { planAnalyzeInfo, operatorAnalyzeInfo } from '../../actions/analyze';
import { filterMapEgList, clearMapFilters } from '../../actions/map';
import { searchWord, usePermission, isToday } from '../../helpers/common';
import OperatorForm from './OperatorForm';

const PersonSubList = (props) => {

    const dispatch = useDispatch();
    const isRequesting = useSelector((state) => state.person.isRequesting);
    const operatorList = useSelector((state) => state.person.operatorList);
    const egList = useSelector((state) => state.map.egList);
    const { rangeId, range } = useSelector((state) => state.analyze.searchParams);

    const [state, setValue] = useState({
        searchText: '',
        selectedOperatorId: null,
        farmId: 0,
        paramOperatorId: null,
        operatorId: null,
        showModal: { add: false, edit: false, delete: false }
    });
    const setState = (params) => { setValue({ ...state, ...params }); }

    const searchInput = React.createRef();
    const { searchText, selectedOperatorId } = state;

    useEffect(() => {
        searchInput.current.focus();
        const { farmId } = props.match.params;

        const match = getMatch();
        let paramOperatorId = null;
        if (match && match.params && match.params.operatorId) paramOperatorId = match.params.operatorId;
        else dispatch(planAnalyzeInfo(farmId));

        setState({ farmId, paramOperatorId });

        return () => dispatch(clearMapFilters());
    }, []);

    useEffect(() => {
        if (!state.farmId) return;
        dispatch(getOperatorList({ farmId: state.farmId }));
    }, [state.farmId, rangeId]);

    useEffect(() => {
        if (operatorList.length > 0 && egList.length > 0 && state.paramOperatorId && !state.selectedOperatorId) {
            const find = operatorList.filter(e => e.person_uuid === state.paramOperatorId);
            if (find.length > 0) handleSelect(find[0]);
        }
    }, [operatorList, egList, state.paramOperatorId, state.selectedOperatorId]);

    const handleSearchBox = (searchText) => {
        setState({ searchText });
    }

    const handleSelect = (item) => {
        const operatorId = item.person_uuid;
        props.history.push(`${props.match.url}/${operatorId}?t=sub&sub=1`);

        if (operatorId === state.selectedOperatorId) {
            setState({ selectedOperatorId: null, paramOperatorId: null });
            dispatch(clearMapFilters());
            dispatch(planAnalyzeInfo(state.farmId));
        } else {
            dispatch(filterMapEgList({ operatorIds: [operatorId] }));
            dispatch(operatorAnalyzeInfo(operatorId, item.person_name));
            setState({ selectedOperatorId: operatorId });
        }
    }

    const getMatch = () => {
        return matchPath(props.history.location.pathname, {
            path: `${props.match.path}/:operatorId?`,
            exact: false,
            strict: false
        });
    }

    const handleOperatorModal = (type, show, id = null) => {
        setState({ showModal: { ...state.showModal, [type]: show }, operatorId: id });
    };

    const handleDeleteConfim = () => {
        const cb = (isSuccess) => {
            if (isSuccess) dispatch(getOperatorList({ farmId: state.farmId }));
        }

        dispatch(deletePerson({
            farm_uuid: state.farmId,
            person_uuid: state.operatorId
        }, cb));
    }

    let filteredList = operatorList;
    const allow = usePermission();
    const isAllowCreate = allow("operator", "create") ? isToday(range) : false;
    const isAllowEdit = allow("operator", "edit");
    const isAllowDelete = allow("operator", "delete");

    if (searchText.trim().length > 0)
        filteredList = operatorList.filter(e => searchWord(e.person_name, searchText));

    return <SubNav>
        <SubNav.Subheader>
            <div className='title-container'>
                <aside className='section-title'>
                    Operators {operatorList.length > 0 ? <>{operatorList.length}</> : ''}
                </aside>
                {isAllowCreate &&
                    <aside className='section-icon-container'>
                        <SubNav.AddButton
                            title="Add Operator"
                            onClick={() => handleOperatorModal("add", true)} />
                    </aside>
                }
            </div>
        </SubNav.Subheader>

        <SubNav.SearchBox
            ref={searchInput}
            placeholder="Operator name"
            value={searchText}
            onChange={e => handleSearchBox(e.target.value)}
            onClear={() => handleSearchBox("")}
        />

        <Tab.Container defaultActiveKey={-1}>
            <Tab.Content>
                <Tab.Pane eventKey={-1} className="scroll-bar-2 ">
                    {isRequesting && <SubNav.Loading />}
                    {!isRequesting && filteredList.length === 0 && <SubNav.NotFound />}
                    {!isRequesting && filteredList.length > 0 &&
                        <SubNav.List>
                            {filteredList.map(item => {
                                const operatorId = item.person_uuid;
                                const selected = (operatorId === selectedOperatorId);

                                return (<SubNav.Item
                                    key={operatorId}
                                    selected={selected}
                                    className="highlight">
                                    <SubNav.Card
                                        className={`${item.alive ? "" : "alive-default"}`}>
                                        <Row className={`p-0 m-0`}>
                                            <Col className="p-0" onClick={() => handleSelect(item)}>
                                                <SubNav.Name
                                                    className="word-wrap"
                                                    bullet={true}>
                                                    {item.person_name}
                                                </SubNav.Name>
                                            </Col>
                                            {isToday(range) && item.alive && <Col className="p-0" md={2} lg={2} sm={2}>
                                                <SubNav.ButtonGroup>
                                                    {isAllowEdit && <SubNav.EditButton
                                                        onClick={() => handleOperatorModal("edit", true, item.person_uuid)} />}
                                                    {isAllowDelete && <SubNav.DeleteButton
                                                        onClick={() => handleOperatorModal("delete", true, item.person_uuid)} />}
                                                </SubNav.ButtonGroup>
                                            </Col>}
                                        </Row>
                                        <Row className={`p-0 m-0`} onClick={() => handleSelect(item)}>
                                            <SubNav.DateRange
                                                start={item.start_date}
                                                end={item.end_date}
                                                alive={item.alive}
                                            />
                                        </Row>
                                    </SubNav.Card>
                                </SubNav.Item>);
                            })}
                        </SubNav.List>}
                </Tab.Pane>
            </Tab.Content>
            {state.showModal.delete && state.operatorId && <Modal.Confirm
                show={true}
                onConfirm={handleDeleteConfim}
                iconClassName="fa fa-trash"
                body={`Do you really want to delete these 
                ${operatorList.find(item => item.person_uuid === state.operatorId).person_name}?`}
                onHide={() => handleOperatorModal("delete", false, null)} />}
            {(state.showModal.add || (state.showModal.edit && state.operatorId)) && <OperatorForm
                onHide={() => handleOperatorModal(state.showModal.edit ? "edit" : "add", false, null)}
                farmId={state.farmId}
                isEditMode={state.showModal.edit}
                info={state.operatorId ? operatorList.find(item => item.person_uuid === state.operatorId) : null}
            />}
        </Tab.Container>
    </SubNav >
}

export default PersonSubList;